// Utility functions for URL handling

/**
 * Convert event name to URL-friendly slug
 * @param {string} eventName - The event name to convert
 * @returns {string} URL-friendly slug
 */
export const createEventSlug = (eventName) => {
  if (!eventName) return '';
  
  return eventName
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Remove consecutive hyphens
    .replace(/-+/g, '-');
};

/**
 * Convert URL slug back to a format that can match event names
 * @param {string} slug - The URL slug to convert
 * @returns {string} Readable format for matching
 */
export const slugToSearchTerm = (slug) => {
  if (!slug) return '';
  
  return slug
    .replace(/-/g, ' ')
    .toLowerCase()
    .trim();
};

/**
 * Find event by slug in an array of events
 * @param {Array} events - Array of event objects
 * @param {string} slug - The URL slug to match
 * @returns {Object|null} Found event or null
 */
export const findEventBySlug = (events, slug) => {
  if (!events || !slug) return null;
  
  const searchTerm = slugToSearchTerm(slug);
  
  return events.find(event => {
    if (!event.title) return false;
    
    // First try to match against API slug, then fallback to generated slug from title
    const apiSlug = event.slug;
    const generatedSlug = createEventSlug(event.title);
    
    return apiSlug === slug || 
           generatedSlug === slug || 
           event.title.toLowerCase().includes(searchTerm) ||
           searchTerm.includes(event.title.toLowerCase());
  });
};

/**
 * Check if an event is over based on its datetime
 * @param {string} datetime - The event datetime string
 * @param {string} originalDate - Optional original date string (e.g., "2026-01-03T00:00:00.000Z")
 * @returns {boolean} True if event is over, false otherwise
 */
export const isEventOver = (datetime, originalDate = null) => {
  if (!datetime) {
    // If no datetime but we have originalDate, use that
    if (originalDate) {
      try {
        const eventDate = new Date(originalDate);
        if (!isNaN(eventDate.getTime())) {
          const now = new Date();
          return eventDate.getTime() < now.getTime();
        }
      } catch (error) {
        console.error('Error parsing originalDate:', error);
      }
    }
    return false;
  }
  
  try {
    let eventDate;
    
    // Check if datetime string has invalid hour (e.g., T26:00:00)
    const hourMatch = datetime.match(/T(\d{2}):/);
    if (hourMatch) {
      const hour = parseInt(hourMatch[1], 10);
      if (hour > 23) {
        // Invalid hour detected, use originalDate if available
        if (originalDate) {
          console.warn('Invalid datetime hour detected, using original date:', datetime, '->', originalDate);
          eventDate = new Date(originalDate);
          if (!isNaN(eventDate.getTime())) {
            const now = new Date();
            return eventDate.getTime() < now.getTime();
          }
        }
        console.warn('Invalid event datetime (hour > 23):', datetime);
        return false;
      }
    }
    
    // Check if datetime has timezone info
    const hasTimezone = datetime.includes('Z') || 
                        datetime.includes('+') || 
                        (datetime.includes('-') && datetime.lastIndexOf('-') > 10);
    
    if (!hasTimezone && datetime.includes('T')) {
      // If it's a date-time without timezone, treat it as UTC by appending 'Z'
      eventDate = new Date(datetime + 'Z');
    } else if (!hasTimezone) {
      // If it's just a date, treat it as UTC midnight
      eventDate = new Date(datetime + 'T00:00:00Z');
    } else {
      // It has timezone info, parse normally
      eventDate = new Date(datetime);
    }
    
    const now = new Date();
    
    if (isNaN(eventDate.getTime())) {
      // If parsing failed and we have originalDate, try that
      if (originalDate) {
        console.warn('Failed to parse datetime, trying original date:', datetime, '->', originalDate);
        eventDate = new Date(originalDate);
        if (!isNaN(eventDate.getTime())) {
          return eventDate.getTime() < now.getTime();
        }
      }
      console.warn('Invalid event date:', datetime);
      return false; // If date is invalid, assume event is not over
    }
    
    // Compare dates - event is over if the event date/time is before now
    // Compare using getTime() which returns milliseconds since epoch (UTC-based)
    return eventDate.getTime() < now.getTime();
  } catch (error) {
    console.error('Error checking if event is over:', error, datetime);
    // Last resort: try originalDate if available
    if (originalDate) {
      try {
        const eventDate = new Date(originalDate);
        if (!isNaN(eventDate.getTime())) {
          const now = new Date();
          return eventDate.getTime() < now.getTime();
        }
      } catch (e) {
        // Ignore
      }
    }
    return false; // On error, assume event is not over
  }
};

/**
 * Get event data from sessionStorage and find by slug
 * @param {string} slug - The URL slug to match
 * @returns {Object|null} Found event or null
 */
export const getEventBySlugFromStorage = (slug) => {
  try {
    // Try to get from individual event storage first (most reliable)
    const eventKey = `event_${slug}`;
    const individualEvent = sessionStorage.getItem(eventKey);
    if (individualEvent) {
      return JSON.parse(individualEvent);
    }
    
    // Try to get from selectedEvent
    const selectedEvent = sessionStorage.getItem("selectedEvent");
    if (selectedEvent) {
      const event = JSON.parse(selectedEvent);
      // Check both API slug and generated slug
      const apiSlug = event.slug;
      const generatedSlug = createEventSlug(event.title);
      if (apiSlug === slug || generatedSlug === slug) {
        return event;
      }
    }
    
    // Try to get from events list
    const eventsData = sessionStorage.getItem("eventsData");
    if (eventsData) {
      const events = JSON.parse(eventsData);
      return findEventBySlug(events, slug);
    }
    
    return null;
  } catch (error) {
    console.error('Error getting event from storage:', error);
    return null;
  }
};
